import {
  getReadonlyListItemCurrency,
  getReadonlyListItemDate,
  getReadonlyListItemId,
  getReadonlyListItemNumber,
  getReadonlyListItemPill,
  getReadonlyListItemString,
  getReadonlyListItemTranslatedString,
  getReadonlyListItemYesOrNo,
} from '@interxfi/app-components/components/readonly-list/util';
import type { ReadonlyListItemRecord } from '@interxfi/design-system/components/list';
import {
  getOneOfType,
  type IxcCorpactionsV1CostCalculationType,
  type IxcCorpactionsV1CustomerAssignment,
  type IxcCorpactionsV1Header,
  type IxcCorpactionsV1RoundingRule,
} from '@interxfi/types';
import { getBooleanFromUndefinedOrNullable } from '@interxfi/utilities/object/access';
import { exhaustiveCheck } from '@interxfi/utilities/types/exhaustive';
import { getCorpactCustomerActionCashDeltaListItems, getCorpactCustomerActionSecurityDeltaListItems } from './action/corpact-action-list-items';
import { CorpactActionTypePill } from './action/corpact-action-type-pill';

export function getCorpactCostCalculationTypeListItems(data: IxcCorpactionsV1CostCalculationType | undefined, prefix = '') {
  const typeNameItem = {
    typeName: getReadonlyListItemTranslatedString(`${prefix}typeName`, 'Type', {
      namespace: 'ixcCorpactionsV1',
      oneOfName: 'IxcCorpactionsV1CostCalculationType',
      value: getOneOfType(data),
    }),
  } as const satisfies ReadonlyListItemRecord;

  if (!data) {
    return typeNameItem;
  }

  if ('quantityAdjustment' in data) {
    return {
      ...typeNameItem,
      quantityAdjustment: {
        '!type': 'quantityAdjustment',
        value: {
          quantityAdjustment: getReadonlyListItemYesOrNo(
            `${prefix}quantityAdjustment.quantityAdjustment`,
            'Quantity Adjustment',
            getBooleanFromUndefinedOrNullable(data.quantityAdjustment, 'quantityAdjustment'),
          ),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('noCostTransfer' in data) {
    return {
      ...typeNameItem,
      noCostTransfer: {
        '!type': 'noCostTransfer',
        value: {
          noCostTransfer: getReadonlyListItemYesOrNo(
            `${prefix}noCostTransfer.noCostTransfer`,
            'No Cost Transfer',
            getBooleanFromUndefinedOrNullable(data.noCostTransfer, 'noCostTransfer'),
          ),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('fixedRate' in data) {
    return {
      ...typeNameItem,
      fixedRate: {
        '!type': 'fixedRate',
        value: {
          perShareCost: getReadonlyListItemCurrency(`${prefix}fixedRate.perShareCost`, 'Per-Share Cost', data.fixedRate.perShareCost),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('costTransfer' in data) {
    return {
      ...typeNameItem,
      costTransfer: {
        '!type': 'costTransfer',
        value: {
          rate: getReadonlyListItemNumber(`${prefix}costTransfer.rate`, 'Rate', data.costTransfer.rate),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('cashToBoot' in data) {
    return {
      ...typeNameItem,
      cashToBoot: {
        '!type': 'cashToBoot',
        value: getReadonlyListItemYesOrNo(`${prefix}cashToBoot.cashToBoot`, 'Cash to Boot', getBooleanFromUndefinedOrNullable(data, 'cashToBoot')),
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  exhaustiveCheck(data['!type']);

  return typeNameItem;
}

export function getCorpactRoundingRuleListItems(data: IxcCorpactionsV1RoundingRule | undefined, prefix = '') {
  return {
    method: getReadonlyListItemTranslatedString(`${prefix}method`, 'Method', {
      namespace: 'ixcCorpactionsV1',
      enumName: 'IxcCorpactionsV1RoundingMethod',
      value: data?.method,
    }),
    places: getReadonlyListItemNumber(`${prefix}places`, 'Places', data?.places),
  } as const satisfies ReadonlyListItemRecord;
}

export function getCorpactCustomerAssignmentListItems(data: IxcCorpactionsV1CustomerAssignment | undefined, prefix = '') {
  const typeNameItem = {
    typeName: getReadonlyListItemTranslatedString(`${prefix}typeName`, 'Type', {
      namespace: 'ixcCorpactionsV1',
      oneOfName: 'IxcCorpactionsV1CustomerAssignment',
      value: getOneOfType(data),
    }),
  } as const satisfies ReadonlyListItemRecord;

  if (!data) {
    return typeNameItem;
  }

  if ('main' in data) {
    return {
      ...typeNameItem,
      main: {
        '!type': 'main',
        value: {
          cashDeltas: data.main.cashDeltas?.map((delta, index) => getCorpactCustomerActionCashDeltaListItems(delta, `${prefix}cashDeltas.${index}.`)),
          securityDeltas: data.main.securityDeltas?.map((delta, index) =>
            getCorpactCustomerActionSecurityDeltaListItems(delta, `${prefix}securityDeltas.${index}.`),
          ),
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  if ('cashInLieu' in data) {
    return {
      ...typeNameItem,
      cashInLieu: {
        '!type': 'cashInLieu',
        value: {
          assignmentId: getReadonlyListItemId(`${prefix}cashInLieu.assignmentId`, 'Assignment ID', data.cashInLieu.assignmentId),
          detail: data.cashInLieu.detail
            ? {
                rate: getReadonlyListItemNumber(`${prefix}cashInLieu.detail.rate`, 'Rate', data.cashInLieu.detail.rate),
                timeApplicable: getReadonlyListItemDate(
                  `${prefix}cashInLieu.detail.timeApplicable`,
                  'Time Applicable',
                  data.cashInLieu.detail.timeApplicable,
                  'timestamp_iso_ms',
                ),
              }
            : undefined,
          inLieuOf: data.cashInLieu.inLieuOf
            ? {
                rate: getReadonlyListItemNumber(`${prefix}cashInLieu.inLieuOf.rate`, 'Rate', data.cashInLieu.inLieuOf.rate),
                costCalculation: (data.cashInLieu.inLieuOf.costCalculation
                  ? getCorpactCostCalculationTypeListItems(data.cashInLieu.inLieuOf.costCalculation, `${prefix}cashInLieu.inLieuOf.costCalculation.`)
                  : undefined) as ReturnType<typeof getCorpactCostCalculationTypeListItems>,
              }
            : undefined,
          rounding: data.cashInLieu.rounding ? getCorpactRoundingRuleListItems(data.cashInLieu.rounding, `${prefix}cashInLieu.rounding.`) : undefined,
        },
      },
    } as const satisfies ReadonlyListItemRecord;
  }

  exhaustiveCheck(data['!type']);

  return typeNameItem;
}

export function getCorpactHeaderListItems(data: IxcCorpactionsV1Header | undefined, prefix = '') {
  return {
    cusip: getReadonlyListItemString(`${prefix}cusip`, 'CUSIP', data?.cusip),
    recordDate: getReadonlyListItemDate(`${prefix}recordDate`, 'Record Date', data?.recordDate, 'date_iso'),
    effectiveDate: getReadonlyListItemDate(`${prefix}effectiveDate`, 'Effective Date', data?.effectiveDate, 'date_iso'),
    exDate: getReadonlyListItemDate(`${prefix}exDate`, 'Ex-Date', data?.exDate, 'date_iso'),
    paymentDate: getReadonlyListItemDate(`${prefix}paymentDate`, 'Payment Date', data?.paymentDate, 'date_iso'),
    cumDate: getReadonlyListItemDate(`${prefix}cumDate`, 'Cum Date', data?.cumDate, 'date_iso'),
    timeApplicable: getReadonlyListItemDate(`${prefix}timeApplicable`, 'Time Applicable', data?.timeApplicable, 'timestamp_iso_ms'),
    actionType: getReadonlyListItemTranslatedString(`${prefix}actionType`, 'Action Type', {
      namespace: 'ixcCorpactionsV1',
      enumName: 'IxcCorpactionsV1ActionType',
      value: data?.actionType,
    }),
    actionTypePill: getReadonlyListItemPill(
      `${prefix}actionTypePill`,
      'Action Type',
      data?.actionType ? <CorpactActionTypePill actionType={data.actionType} /> : undefined,
    ),
    paymentType: getReadonlyListItemTranslatedString(`${prefix}paymentType`, 'Payment Type', {
      namespace: 'ixcCorpactionsV1',
      enumName: 'IxcCorpactionsV1PaymentType',
      value: data?.paymentType,
    }),
    actionTypeLabel: getReadonlyListItemString(`${prefix}actionTypeLabel`, 'Action Type Label', data?.actionTypeLabel),
    actionTypeSubLabel: getReadonlyListItemString(`${prefix}actionTypeSubLabel`, 'Action Type Sub-Label', data?.actionTypeSubLabel),
    description: getReadonlyListItemString(`${prefix}description`, 'Description', data?.description),
    taxYear: getReadonlyListItemString(`${prefix}taxYear`, 'Tax Year', data?.taxYear),
  } as const satisfies ReadonlyListItemRecord;
}
